import { AlertTriangle, MapPin, Monitor } from 'lucide-react';
import { formatCount, formatMoney } from '@/shared/lib/format/money';
import type { BillboardRow } from '../PreviewStep.types';

type SelectedScreensTableProps = {
  screens: BillboardRow[];
};

function shortLocation(location: string): string {
  if (!location || location === '—') return '—';
  const parts = location
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.join(', ');
}

function OrientationBadge({ value }: { value?: string }) {
  if (!value) return null;
  const label = value.charAt(0) + value.slice(1).toLowerCase();
  return (
    <span className="rounded-full bg-[var(--dash-soft)] px-1.5 py-px text-[10px] font-semibold text-[var(--dash-muted)]">
      {label}
    </span>
  );
}

function ScreenAvatar({ row }: { row: BillboardRow }) {
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[var(--dash-soft)] text-[var(--dash-brand)] ring-1 ring-black/[0.05]">
      {row.thumbnail ? (
        <img
          src={row.thumbnail}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <Monitor className="h-4 w-4" />
      )}
    </span>
  );
}

function NoPlays() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10.5px] font-semibold text-amber-700">
      <AlertTriangle className="h-3 w-3" />
      No plays
    </span>
  );
}

export function SelectedScreensTable({ screens }: SelectedScreensTableProps) {
  const totalPrice = screens.reduce((sum, row) => sum + (row.price || 0), 0);
  const totalReps = screens.reduce(
    (sum, row) => sum + (row.reps != null ? row.reps : 0),
    0,
  );
  const hasAnyReps = screens.some((row) => row.reps != null);
  const maxReps = Math.max(1, ...screens.map((r) => r.reps || 0));
  const emptyCount = screens.filter((r) => r.reps === 0).length;

  return (
    <section className="camp-section min-w-0 text-left !py-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-[var(--dash-brand)]" />
          <h2 className="m-0 text-[13px] font-semibold text-[var(--dash-ink)]">
            Selected screens
          </h2>
          <span className="rounded-md bg-[var(--dash-soft)] px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-[var(--dash-muted)]">
            {screens.length}
          </span>
        </div>
        {hasAnyReps ? (
          <p className="m-0 text-[11.5px] text-[var(--dash-muted)]">
            {formatCount(totalReps)} plays · {formatMoney(totalPrice)}
          </p>
        ) : null}
      </div>

      {emptyCount > 0 ? (
        <p className="m-0 mb-3 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-snug text-amber-800">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
          {emptyCount} screen{emptyCount === 1 ? ' has' : 's have'} no plays
          booked and will not be charged or shown. Go back to Schedule &amp;
          Slots to add hours.
        </p>
      ) : null}

      {screens.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--dash-line)] py-6 text-center">
          <p className="m-0 text-sm font-medium text-[var(--dash-muted)]">
            No screens selected
          </p>
        </div>
      ) : (
        <>
          <ul className="m-0 flex max-h-[520px] list-none flex-col overflow-y-auto p-0 md:hidden">
            {screens.map((row) => (
              <li
                key={row.key}
                className="flex items-start gap-3 border-b border-[var(--dash-line)]/70 py-2.5 last:border-0"
              >
                <ScreenAvatar row={row} />
                <div className="min-w-0 flex-1">
                  <p className="m-0 flex flex-wrap items-center gap-1.5 text-[13px] font-semibold text-[var(--dash-ink)]">
                    <span className="min-w-0 break-words">{row.screenName}</span>
                    <OrientationBadge value={row.orientation} />
                  </p>
                  <p className="m-0 mt-0.5 text-[11.5px] leading-snug break-words text-[var(--dash-muted)]">
                    {shortLocation(row.location)}
                  </p>
                  <p className="m-0 mt-1 text-[11.5px] text-[var(--dash-muted)]">
                    {row.reps === 0 ? (
                      <NoPlays />
                    ) : row.reps != null ? (
                      `${formatCount(row.reps)} plays`
                    ) : null}
                  </p>
                </div>
                <p className="m-0 shrink-0 text-[13px] font-semibold tabular-nums text-[var(--dash-ink)]">
                  {formatMoney(row.price)}
                </p>
              </li>
            ))}
            <li className="flex items-center justify-between pt-2 text-[13px] font-bold text-[var(--dash-ink)]">
              <span>
                Total{hasAnyReps ? ` · ${formatCount(totalReps)} plays` : ''}
              </span>
              <span className="tabular-nums">{formatMoney(totalPrice)}</span>
            </li>
          </ul>

          <div className="hidden max-h-[560px] overflow-auto rounded-xl border border-[var(--dash-line)] md:block">
            <table className="min-w-full border-collapse text-left">
              <thead className="sticky top-0 z-[1] bg-[var(--dash-soft)]">
                <tr className="text-[10.5px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
                  <th className="px-3 py-2">Screen</th>
                  <th className="px-3 py-2">Plays</th>
                  <th className="px-3 py-2 text-right">Rate / play</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {screens.map((row) => {
                  const rate =
                    row.reps && row.reps > 0 ? row.price / row.reps : null;
                  return (
                    <tr
                      key={row.key}
                      className="border-t border-[var(--dash-line)]/70 transition-colors hover:bg-[var(--dash-soft)]/60"
                    >
                      <td className="min-w-[220px] px-3 py-2.5 align-middle">
                        <div className="flex items-center gap-3">
                          <ScreenAvatar row={row} />
                          <div className="min-w-0">
                            <p className="m-0 flex flex-wrap items-center gap-1.5 text-[13px] font-semibold text-[var(--dash-ink)]">
                              <span className="break-words">{row.screenName}</span>
                              <OrientationBadge value={row.orientation} />
                            </p>
                            <p className="m-0 mt-0.5 max-w-[360px] text-[11.5px] leading-snug break-words text-[var(--dash-muted)]">
                              {shortLocation(row.location)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="min-w-[130px] px-3 py-2.5 align-middle">
                        {row.reps === 0 ? (
                          <NoPlays />
                        ) : row.reps != null ? (
                          <div className="flex items-center gap-2">
                            <span className="w-9 text-[13px] font-semibold tabular-nums text-[var(--dash-ink)]">
                              {formatCount(row.reps)}
                            </span>
                            <span className="h-1.5 w-20 overflow-hidden rounded-full bg-[var(--dash-soft)]">
                              <span
                                className="block h-full rounded-full bg-[var(--dash-brand)]"
                                style={{
                                  width: `${Math.max(6, (row.reps / maxReps) * 100)}%`,
                                }}
                              />
                            </span>
                          </div>
                        ) : (
                          <span className="text-[13px] text-[var(--dash-muted)]">
                            —
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right align-middle text-[12.5px] whitespace-nowrap text-[var(--dash-muted)] tabular-nums">
                        {rate != null ? formatMoney(rate) : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right align-middle text-[13px] font-semibold whitespace-nowrap text-[var(--dash-ink)] tabular-nums">
                        {formatMoney(row.price)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="sticky bottom-0 bg-white">
                <tr className="border-t-2 border-[var(--dash-line)] text-[13px] font-bold text-[var(--dash-ink)]">
                  <td className="px-3 py-2.5">Total</td>
                  <td className="px-3 py-2.5 tabular-nums">
                    {hasAnyReps ? formatCount(totalReps) : '—'}
                  </td>
                  <td className="px-3 py-2.5" />
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {formatMoney(totalPrice)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
